import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { spotsApi } from '../services/api';
import { useAuth } from './AuthContext';
import logger from '../services/logger';

const SpotsContext = createContext({});

export const SpotsProvider = ({ children }) => {
  const { user } = useAuth();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpot, setSelectedSpot] = useState(null);

  const loadSpots = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const spots = await spotsApi.getSpots();
      setSpots(spots);
    } catch (error) {
      logger.error('Error loading spots:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadSpots();
    }
  }, [user?.id, loadSpots]);

  const addSpot = useCallback(async (spotData) => {
    if (!user?.id) throw new Error('User not authenticated');
    const newSpot = await spotsApi.create(spotData);
    setSpots(prev => [...prev, newSpot]);
    return newSpot;
  }, [user?.id]);

  const updateSpot = useCallback(async (spotId, spotData) => {
    if (!user?.id) throw new Error('User not authenticated');
    const response = await spotsApi.update(spotId, user.id, spotData);
    setSpots(prev => prev.map(s => s.id === spotId ? response.data : s));
    return response.data;
  }, [user?.id]);

  const deleteSpot = useCallback(async (spotId) => {
    if (!user?.id) throw new Error('User not authenticated');
    await spotsApi.delete(spotId, user.id);
    setSpots(prev => prev.filter(s => s.id !== spotId));
    setSelectedSpot(prev => prev?.id === spotId ? null : prev);
  }, [user?.id]);

  const getMaxSpots = useCallback(() => {
    if (user?.plan === 'free') return 3;
    if (user?.plan === 'ultra') return 50;
    if (user?.plan === 'magnum') return 999;
    return 3;
  }, [user?.plan]);

  const canAddSpot = useCallback(() => {
    return spots.length < getMaxSpots();
  }, [spots.length, getMaxSpots]);

  const value = useMemo(() => ({
    spots, loading, selectedSpot, setSelectedSpot,
    addSpot, updateSpot, deleteSpot, loadSpots, getMaxSpots, canAddSpot,
  }), [spots, loading, selectedSpot, addSpot, updateSpot, deleteSpot, loadSpots, getMaxSpots, canAddSpot]);

  return (
    <SpotsContext.Provider value={value}>
      {children}
    </SpotsContext.Provider>
  );
};

export const useSpots = () => useContext(SpotsContext);
